import { useState, useEffect, useRef, useMemo } from 'react'
import { useRouter } from '~/hooks/useRouter'
import { getNewCollectionPalette } from '~/components/ProductPage/productTypes/wardrobeConfigFactory'
import { ColorName } from '~/utils/colorDictionary'
import { useFurniture } from '~/context/furnitureContext'
import { furnitureToConstraintsPatch } from '~/utils/furnitureBySku'

export const WORK_TABLE_WIDTH_RANGE: [number, number] = [100, 180]
export const WORK_TABLE_WIDTH_STEP = 10
export const WORK_TABLE_DEPTH = 60

const WORK_TABLE_DRAWER_WIDTH_THRESHOLD = 120

export function getWorkTableDrawerCount(width: number): 2 | 3 {
  return width <= WORK_TABLE_DRAWER_WIDTH_THRESHOLD ? 2 : 3
}

export const WORK_TABLE_HEIGHT_OPTIONS = [75] as const
export type WorkTableHeight = (typeof WORK_TABLE_HEIGHT_OPTIONS)[number]

export type WorkTableDimensionsComponent = {
  type: 'dimensions'
  width: number
  setWidth: (value: number) => void
  height: WorkTableHeight
  setHeight: (value: WorkTableHeight) => void
  depth: number
  predefinedValue?: {
    width: number
    height: number
    depth: number
  }
}

export type WorkTableTextureComponent = {
  type: 'texture'
  textures: string[]
  selectedTexture: string
  setSelectedTexture: (value: string) => void
}

export type WorkTableDrawersComponent = {
  type: 'drawers'
  hasDrawers: boolean
  setHasDrawers: (value: boolean) => void
}

type WorkTablePriceComponent = {
  type: 'price'
  price: number
  predefinedValue?: number
}

export type ProductComponent =
  | WorkTableDimensionsComponent
  | WorkTableTextureComponent
  | WorkTableDrawersComponent
  | WorkTablePriceComponent

/** Static price estimate for catalog cards (not used in the configurator). */
export function calculateWorkTablePrice(
  width: number,
  _height: number,
  hasDrawers: boolean
): number {
  const BASE = 5200
  const PER_CM = 20
  const PER_DRAWER = 1000
  const widthExtra = (width - WORK_TABLE_WIDTH_RANGE[0]) * PER_CM
  const drawerExtra = hasDrawers
    ? getWorkTableDrawerCount(width) * PER_DRAWER
    : 0
  return BASE + widthExtra + drawerExtra
}

export const WORK_TABLE_DEFAULTS = {
  width: 140,
  height: 75 as WorkTableHeight,
  hasDrawers: false,
  // Must be a colour that maps to a real work-table chipboard (GreenSalvia/U638
  // is not in the backend palette).
  texture: ColorName.BlackBrown as string,
} as const

const isValidHeight = (v: number): v is WorkTableHeight =>
  (WORK_TABLE_HEIGHT_OPTIONS as readonly number[]).includes(v)

export const WorkTableConfiguration: () => ProductComponent[] = () => {
  const router = useRouter()
  const hasInitialized = useRef(false)

  const furniture = useFurniture()
  const textures = useMemo(() => {
    const patch = furniture ? furnitureToConstraintsPatch(furniture) : null
    return patch?.colors?.length ? patch.colors : getNewCollectionPalette()
  }, [furniture])

  const [width, setWidth] = useState<number>(WORK_TABLE_DEFAULTS.width)
  const [height, setHeight] = useState<WorkTableHeight>(
    WORK_TABLE_DEFAULTS.height
  )
  const [hasDrawers, setHasDrawers] = useState<boolean>(
    WORK_TABLE_DEFAULTS.hasDrawers
  )
  const [selectedTexture, setSelectedTexture] = useState<string>(
    WORK_TABLE_DEFAULTS.texture
  )

  useEffect(() => {
    if (!router.isReady || hasInitialized.current) return
    hasInitialized.current = true

    const q = router.query
    if (q.width) {
      const parsed = Number(q.width)
      if (Number.isFinite(parsed)) setWidth(parsed)
    }

    if (q.height) {
      const parsed = Number(q.height)
      if (isValidHeight(parsed)) setHeight(parsed)
    }

    if (
      typeof q.texture === 'string' &&
      textures.includes(q.texture)
    ) {
      setSelectedTexture(q.texture)
    }

    if (typeof q.drawers === 'string') {
      setHasDrawers(q.drawers === 'true' || q.drawers === '1')
    }
  }, [router.isReady, router.query, textures])

  const validTexture =
    textures.includes(selectedTexture) ? selectedTexture : textures[0] ?? WORK_TABLE_DEFAULTS.texture

  return [
    {
      type: 'dimensions',
      width,
      setWidth,
      height,
      setHeight,
      depth: WORK_TABLE_DEPTH,
    },
    {
      type: 'texture',
      textures,
      selectedTexture: validTexture,
      setSelectedTexture,
    },
    {
      type: 'drawers',
      hasDrawers,
      setHasDrawers,
    },
    {
      type: 'price',
      price: 0,
    },
  ]
}
